import React, { useState, useCallback, useRef, useEffect } from "react";
import { InputNumber, Input, Tooltip } from "antd";
import SensorModal from "./SensorModal";
import ConfirmationAlertCross from "./confirm-alert-cross";
import {
  PlusCircle,
  XCircle,
  Camera,
  Edit3,
  Check,
  X,
} from "feather-icons-react/build/IconComponents";
import ImageWithBasePath from "../core/img/imagewithbasebath";
import { useTranslation } from "react-i18next";
import CameraLocationModal from "./CameraLocationModal";
import httpProductService from "../services/http/products/product.service";
import notificationService from "../services/toaster.service";

const Shelf = ({
  id,
  rackId,
  rackName,
  name,
  sensors = [],
  setRacks,
  cameras = [],
  cameras_location = {},
  onDelete,
  onChange,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentSensorNumber, setCurrentSensorNumber] = useState(null);
  const [showCameraLocations, setShowCameraLocations] = useState(false);

  // Editing states
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [nameError, setNameError] = useState("");

  const nameInputRef = useRef(null);
  const { t } = useTranslation();
  const [editingProductId, setEditingProductId] = useState(null);
  const [tempWeight, setTempWeight] = useState("");

  const handleWeightClick = (product) => {
    setEditingProductId(product.id);
    setTempWeight(product.actual_zemic_sensor_weight || "0");
  };

  const onWeightUpdate = async (id, weight) => {
    const formData = new FormData();
    formData.append("id", id);
    formData.append("actual_zemic_sensor_weight", weight);
    return new Promise((resolve, reject) => {
      httpProductService
        .updateProduct(formData)
        .then((response) => {
          resolve(true);
        })
        .catch((error) => {
          resolve(false);
        });
    });
  };

  const handleWeightSave = async (productId) => {
    const isUpdated = await onWeightUpdate(productId, tempWeight);
    console.log("is updated: ", isUpdated);

    if (isUpdated) {
      notificationService.successMessage(t("product.productUpdateSuccess"));

      setRacks((prev) =>
        prev.map((rack) =>
          rack.id === rackId
            ? {
                ...rack,
                shelves: rack.shelves.map((shelf) =>
                  shelf.id === id
                    ? {
                        ...shelf,
                        sensors: shelf.sensors.map((sensor) => ({
                          ...sensor,
                          products: sensor.products.map((product) =>
                            product.id === productId
                              ? {
                                  ...product,
                                  actual_zemic_sensor_weight: tempWeight,
                                }
                              : product
                          ),
                        })),
                      }
                    : shelf
                ),
              }
            : rack
        )
      );
      //onChange?.();
    }

    setEditingProductId(null);
    setTempWeight("");
  };

  const handleWeightCancel = () => {
    setEditingProductId(null);
    setTempWeight("");
  };

  const handleSensorKeyPress = (e, productId) => {
    if (e.key === "Enter") {
      handleWeightSave(productId);
    } else if (e.key === "Escape") {
      handleWeightCancel();
    }
  };

  // Initialize edited name when name prop changes
  useEffect(() => {
    setEditedName(getShelfNumber());
  }, [name]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      setTimeout(() => {
        nameInputRef.current.focus();
        nameInputRef.current.select();
      }, 100);
    }
  }, [isEditingName]);

  // Get shelf number from name (remove "Shelf " prefix if exists)
  const getShelfNumber = useCallback(() => {
    const match = name?.match(/^Shelf (\d+)$/);
    return match ? match[1] : name || "";
  }, [name]);

  // Validate shelf number
  const validateShelfNumber = useCallback((value) => {
    if (!value || !value.toString().trim()) {
      return "Shelf number is required";
    }
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1) {
      return "Shelf number must be a positive number";
    }
    return null;
  }, []);

  // Start editing shelf name
  const startEditingName = useCallback((e) => {
    e.stopPropagation();
    setIsEditingName(true);
    setNameError("");
  }, []);

  // Save shelf name
  const saveShelfName = useCallback(() => {
    const error = validateShelfNumber(editedName);
    if (error) {
      setNameError(error);
      return;
    }

    const newName = `Shelf ${parseInt(editedName)}`;

    setRacks((prev) =>
      prev.map((rack) =>
        rack.id === rackId
          ? {
              ...rack,
              shelves: rack.shelves.map((shelf) =>
                shelf.id === id ? { ...shelf, name: newName } : shelf
              ),
            }
          : rack
      )
    );

    onChange?.();
    setIsEditingName(false);
    setNameError("");
  }, [editedName, validateShelfNumber, setRacks, rackId, id, onChange]);

  // Cancel editing
  const cancelEditingName = useCallback(() => {
    setEditedName(getShelfNumber());
    setIsEditingName(false);
    setNameError("");
  }, [getShelfNumber]);

  // Handle key press
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter") {
        saveShelfName();
      } else if (e.key === "Escape") {
        cancelEditingName();
      }
    },
    [saveShelfName, cancelEditingName]
  );

  const handleSensorClick = (sensorNumber) => {
    setCurrentSensorNumber(sensorNumber);
    setModalIsOpen(true);
  };

  const addSensor = () => {
    setRacks((prev) =>
      prev.map((rack) =>
        rack.id === rackId
          ? {
              ...rack,
              shelves: rack.shelves.map((shelf) =>
                shelf.id === id
                  ? {
                      ...shelf,
                      sensors: [
                        ...shelf.sensors,
                        {
                          id: `temp_sensor_${Date.now()}_${Math.random()
                            .toString(36)
                            .substr(2, 9)}`,
                          sensor_number: shelf.sensors.length + 1,
                          products: [],
                        },
                      ],
                    }
                  : shelf
              ),
            }
          : rack
      )
    );
    onChange?.();
  };

  const handleDeleteShelf = () => {
    if (onDelete) {
      onDelete(id);
    }
  };

  const deleteSensor = (sensorId) => {
    setRacks((prev) =>
      prev.map((rack) =>
        rack.id === rackId
          ? {
              ...rack,
              shelves: rack.shelves.map((shelf) =>
                shelf.id === id
                  ? {
                      ...shelf,
                      sensors: shelf.sensors.filter(
                        (sensor) => sensor.id !== sensorId
                      ),
                    }
                  : shelf
              ),
            }
          : rack
      )
    );
    onChange?.();
  };

  const totalProductCount = sensors.reduce(
    (productCount, sensor) => productCount + (sensor.products?.length || 0),
    0
  );

  const totalProductQuantity = sensors.reduce(
    (quantityCount, sensor) =>
      quantityCount +
      (sensor.products?.reduce(
        (sum, product) => sum + (parseInt(product.quantity) || 0),
        0
      ) || 0),
    0
  );

  const handleCameraLocations = (locations) => {
    setRacks((prev) =>
      prev.map((rack) =>
        rack.id === rackId
          ? {
              ...rack,
              shelves: rack.shelves.map((shelf) =>
                shelf.id === id
                  ? {
                      ...shelf,
                      cameras_location: locations,
                    }
                  : shelf
              ),
            }
          : rack
      )
    );
    onChange?.();
  };

  return (
    <div
      className="shelf-container mb-2"
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #e8ecf0",
        borderRadius: "8px",
        padding: "10px 12px",
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        transition: "all 0.2s ease",
      }}
      onMouseEnter={(e) => {
        e.target.style.borderColor = "#d1d7dd";
      }}
      onMouseLeave={(e) => {
        e.target.style.borderColor = "#e8ecf0";
      }}
    >
      {/* Shelf Header */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          {/* Editable Shelf Name */}
          {isEditingName ? (
            <div className="d-flex align-items-center">
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#495057",
                  marginRight: "6px",
                }}
              >
                Shelf
              </span>
              <div style={{ position: "relative" }}>
                <InputNumber
                  ref={nameInputRef}
                  value={editedName}
                  onChange={(value) => {
                    setEditedName(value);
                    if (nameError) setNameError("");
                  }}
                  onKeyDown={handleKeyPress}
                  size="small"
                  min={1}
                  controls={false}
                  style={{
                    width: "60px",
                    fontSize: "13px",
                    fontWeight: "600",
                    border: nameError
                      ? "1px solid #dc3545"
                      : "1px solid #007bff",
                    boxShadow: nameError
                      ? "0 0 0 2px rgba(220, 53, 69, 0.1)"
                      : "0 0 0 2px rgba(0, 123, 255, 0.1)",
                  }}
                  placeholder="Enter number"
                />

                {/* Action Buttons */}
                {/*   <div
                  style={{
                    position: "absolute",
                    right: "-60px",
                    top: "0",
                    display: "flex",
                    gap: "4px",
                  }}
                >
                  <button
                    onClick={saveShelfName}
                    title="Save changes"
                    style={{
                      padding: "2px 4px",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "3px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "10px",
                    }}
                  >
                    <Check size={10} />
                  </button>
                  <button
                    onClick={cancelEditingName}
                    title="Cancel changes"
                    style={{
                      padding: "2px 4px",
                      backgroundColor: "#6c757d",
                      color: "white",
                      border: "none",
                      borderRadius: "3px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "10px",
                    }}
                  >
                    <X size={10} />
                  </button>
                </div> */}

                {/* Error Message */}
                {nameError && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: "0",
                      right: "0",
                      fontSize: "9px",
                      color: "#dc3545",
                      backgroundColor: "rgba(220, 53, 69, 0.1)",
                      padding: "2px 4px",
                      borderRadius: "3px",
                      marginTop: "2px",
                      border: "1px solid rgba(220, 53, 69, 0.2)",
                      zIndex: 10,
                    }}
                  >
                    {nameError}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <h6
              className="mb-0 d-flex align-items-center"
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#495057",
                cursor: "pointer",
                padding: "2px 4px",
                borderRadius: "4px",
                transition: "all 0.2s ease",
              }}
              onClick={startEditingName}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f8f9fa";
                e.target.style.color = "#007bff";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#495057";
              }}
            >
              {name}
              <Edit3 size={10} style={{ marginLeft: "4px", opacity: 0.6 }} />
            </h6>
          )}

          {/* Stats */}
          <span
            className="text-muted ms-2"
            style={{ fontSize: "10px", lineHeight: "1.2" }}
          >
            ({t("shelf.sensors")}: <strong>{sensors.length}</strong>,{" "}
            {t("shelf.product")}: <strong>{totalProductCount}</strong>,{" "}
            {t("shelf.totalQuantity")}: <strong>{totalProductQuantity}</strong>)
          </span>
        </div>

        {/* Action Buttons */}
        <div className="d-flex align-items-center" style={{ gap: "6px" }}>
          {/* Camera Location Button */}
          <Tooltip title="Configure camera locations">
            <div
              className="camera-icon-wrapper"
              onClick={() => setShowCameraLocations(true)}
              style={{
                cursor: "pointer",
                padding: "4px",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                backgroundColor: "#f8f9fa",
                transition: "all 0.2s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "24px",
                minHeight: "24px",
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = "#007bff";
                e.target.style.backgroundColor = "#e3f2fd";
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = "#dee2e6";
                e.target.style.backgroundColor = "#f8f9fa";
              }}
            >
              <ImageWithBasePath
                src="assets/img/icons/cctv-camera-icon.png"
                alt="img"
                height={"15px"}
              />
            </div>
          </Tooltip>

          {/* Delete Shelf Button */}
          <ConfirmationAlertCross
            title="Delete Shelf?"
            text={`This will permanently delete "${name}" and all its ${sensors.length} sensors with their products. This action cannot be undone.`}
            onConfirm={handleDeleteShelf}
            confirmText="Delete Shelf"
            cancelText="Cancel"
          />
        </div>
      </div>

      {/* Sensors Row */}
      <div
        className="sensors-row"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "flex-start",
        }}
      >
        {sensors.map((sensor, index) => (
          <div
            key={sensor.sensor_number}
            className="sensor-wrapper"
            style={{
              position: "relative",
              display: "inline-block",
              minWidth: "130px",
            }}
          >
            <button
              className="sensor-btn btn btn-outline-primary"
              style={{
                textAlign: "center",
                width: "100%",
              }}
              onClick={() => handleSensorClick(sensor.sensor_number)}
            >
              <div className="mt-3">
                {sensor.products && sensor.products.length > 0 ? (
                  <div
                    className="d-flex flex-wrap gap-3"
                    style={{
                      fontSize: "12px",
                      marginTop: "5px",
                      color: "#555",
                    }}
                  >
                    {sensor.products.map((product, index) => (
                      <div
                        key={`sensor_products${product.id}`}
                        className="d-flex flex-column align-items-center"
                      >
                        {/* Product Image and Quantity */}
                        <div className="d-flex align-items-center mb-1">
                          <div
                            title={`LM Number: ${product?.lm_product_number} \nEAN: ${product?.ean}\nName: ${product?.name}`}
                          >
                            <ImageWithBasePath
                              type="link"
                              src={product?.main_product_image}
                              alt="Product"
                              style={{
                                width: 30,
                                height: 30,
                                marginRight: 5,
                                borderRadius: "4px",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                          <span className="me-1">x</span>
                          <span className="fw-bolder">
                            {product.quantity || 0}
                          </span>
                        </div>

                        {/* Editable Weight */}
                        <div
                          className="text-center"
                          style={{ minWidth: "60px" }}
                          onClick={(e) => e.stopPropagation()} // Prevent parent button click
                        >
                          {editingProductId === product.id ? (
                            <div className="d-flex flex-column align-items-center">
                              <input
                                type="number"
                                value={tempWeight}
                                onChange={(e) => setTempWeight(e.target.value)}
                                onKeyDown={(e) =>
                                  handleSensorKeyPress(e, product.id)
                                }
                                onBlur={() => handleWeightSave(product.id)}
                                autoFocus
                                style={{
                                  width: "50px",
                                  height: "20px",
                                  border: "1px solid #007bff",
                                  borderRadius: "3px",
                                  textAlign: "center",
                                  fontSize: "10px",
                                  outline: "none",
                                  padding: "2px",
                                }}
                                step="0.01"
                                min="0"
                              />
                            </div>
                          ) : (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWeightClick(product);
                              }}
                              style={{
                                cursor: "pointer",
                                padding: "2px 4px",
                                borderRadius: "3px",
                                backgroundColor: "#f8f9fa",
                                border: "1px solid transparent",
                                fontSize: "10px",
                                minHeight: "16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = "#e9ecef";
                                e.target.style.borderColor = "#dee2e6";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = "#f8f9fa";
                                e.target.style.borderColor = "transparent";
                              }}
                              title="Click to edit weight"
                            >
                              {product.actual_zemic_sensor_weight || "0"}g
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p
                    className="text-muted"
                    style={{
                      fontSize: "12px",
                      marginTop: "21px",
                      marginBottom: "10px",
                      color: "#555",
                    }}
                  >
                    {t("shelf.noProductAdded")}
                  </p>
                )}
                <hr className="mt-3 mb-1" />
                Sensor {sensor.sensor_number}
              </div>
            </button>
            <div
              className="delete-btn-wrapper"
              style={{
                position: "absolute",
                top: "2px",
                right: "0px",
              }}
            >
              <ConfirmationAlertCross
                title="Delete Sensor?"
                text="This will delete the sensor and all associated products."
                onConfirm={() => deleteSensor(index)}
              >
                <button
                  className="btn btn-danger btn-sm"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: "3px 6px",
                    borderRadius: "50%",
                    fontSize: "10px",
                  }}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </ConfirmationAlertCross>
            </div>
          </div>
        ))}
        {/* Add Sensor Button */}
        <Tooltip title="Add new sensor">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "40px",
              minHeight: "92px",
              cursor: "pointer",
              borderRadius: "6px",
              border: "2px dashed #b5b5b5ff",
              backgroundColor: "#f8fff8",
              transition: "all 0.2s ease",
            }}
            onClick={addSensor}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#e8f5e8";
              e.target.style.borderColor = "#1e7e34";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#f8fff8";
              e.target.style.borderColor = "#b5b5b5ff";
            }}
          >
            <PlusCircle size={20} color="#b5b5b5ff" />
          </div>
        </Tooltip>
      </div>

      {/* Modals */}
      {modalIsOpen && (
        <SensorModal
          isOpen={modalIsOpen}
          closeModal={() => setModalIsOpen(false)}
          sensorNumber={currentSensorNumber}
          rackNumber={rackId}
          rackName={rackName}
          shelfNumber={name}
          saveProductDetails={(sensorNumber, selectedProducts) => {
            setRacks((prev) =>
              prev.map((rack) =>
                rack.id === rackId
                  ? {
                      ...rack,
                      shelves: rack.shelves.map((shelf) =>
                        shelf.id === id
                          ? {
                              ...shelf,
                              sensors: shelf.sensors.map((sensor) =>
                                sensor.sensor_number === sensorNumber
                                  ? { ...sensor, products: selectedProducts }
                                  : sensor
                              ),
                            }
                          : shelf
                      ),
                    }
                  : rack
              )
            );
            onChange?.();
          }}
        />
      )}

      <CameraLocationModal
        isOpen={showCameraLocations}
        onClose={() => setShowCameraLocations(false)}
        cameras={cameras}
        onSave={handleCameraLocations}
        existingLocations={cameras_location}
      />
    </div>
  );
};

export default Shelf;
